import type { CSSProperties } from "react"

import type { PanelId } from "@/lib/states/panel-width-state"

export interface PanelSpec {
	cssVar: `--${string}`
	min: number
	max: number
	/** Keeps a width saved on a wide monitor from crowding a laptop screen. */
	maxViewportPercent: number
}

export const PANEL_SPECS: Record<PanelId, PanelSpec> = {
	"main-nav": {
		cssVar: "--main-nav-width",
		min: 200,
		max: 360,
		maxViewportPercent: 24,
	},
	"inbox-nav": {
		cssVar: "--inbox-nav-width",
		min: 180,
		max: 360,
		maxViewportPercent: 24,
	},
	"inbox-list": {
		cssVar: "--inbox-list-width",
		min: 260,
		max: 640,
		maxViewportPercent: 42,
	},
	"thread-details": {
		cssVar: "--thread-details-width",
		min: 280,
		max: 560,
		maxViewportPercent: 38,
	},
	// The Agent page's recommendations beside the open one.
	"agent-list": {
		cssVar: "--agent-list-width",
		min: 260,
		max: 640,
		maxViewportPercent: 42,
	},
}

/** Thread list and notification list share this default so the two pages line up. */
export const INBOX_LIST_WIDTH_CLASSES =
	"w-[var(--inbox-list-width,290px)] min-w-[var(--inbox-list-width,290px)] min-[1600px]:w-[var(--inbox-list-width,326px)] min-[1600px]:min-w-[var(--inbox-list-width,326px)]"

/** The conversation keeps this much room while a neighbouring panel grows. */
export const MIN_CONTENT_WIDTH = 440

export const KEYBOARD_STEP = 16
export const KEYBOARD_STEP_LARGE = 64

export function clampPanelWidth(width: number, min: number, max: number) {
	return Math.min(Math.max(width, min), Math.max(min, max))
}

export function panelWidthValue(spec: PanelSpec, width: number) {
	const px = Math.round(clampPanelWidth(width, spec.min, spec.max))
	return `clamp(${spec.min}px, ${spec.maxViewportPercent}vw, ${px}px)`
}

/** Inline style for the element that carries the panel's width variable. */
export function panelWidthStyle(
	spec: PanelSpec,
	width: number | undefined,
): CSSProperties | undefined {
	if (width === undefined || !Number.isFinite(width)) return undefined
	return { [spec.cssVar]: panelWidthValue(spec, width) } as CSSProperties
}

export function maxPanelWidth({
	spec,
	startWidth,
	absorberWidth,
	viewportWidth,
}: {
	spec: PanelSpec
	startWidth: number
	absorberWidth?: number
	viewportWidth: number
}) {
	const byViewport = (viewportWidth * spec.maxViewportPercent) / 100
	const byContent =
		absorberWidth === undefined
			? Infinity
			: startWidth + Math.max(0, absorberWidth - MIN_CONTENT_WIDTH)
	return Math.floor(
		Math.max(spec.min, Math.min(spec.max, byViewport, byContent)),
	)
}

export type PanelSide = "left" | "right"

/** Width after a keyboard press, or null when the key does not resize. */
export function keyboardPanelWidth({
	key,
	shiftKey,
	panelSide,
	current,
	min,
	max,
}: {
	key: string
	shiftKey: boolean
	panelSide: PanelSide
	current: number
	min: number
	max: number
}) {
	const step = shiftKey ? KEYBOARD_STEP_LARGE : KEYBOARD_STEP
	const grow = panelSide === "left" ? "ArrowRight" : "ArrowLeft"
	const shrink = panelSide === "left" ? "ArrowLeft" : "ArrowRight"
	if (key === grow) return clampPanelWidth(current + step, min, max)
	if (key === shrink) return clampPanelWidth(current - step, min, max)
	if (key === "Home") return min
	if (key === "End") return Math.max(min, max)
	return null
}

/** A right-hand panel grows as the pointer moves left. */
export function dragPanelWidth({
	startWidth,
	deltaX,
	panelSide,
	min,
	max,
}: {
	startWidth: number
	deltaX: number
	panelSide: PanelSide
	min: number
	max: number
}) {
	const signed = panelSide === "left" ? deltaX : -deltaX
	return Math.round(clampPanelWidth(startWidth + signed, min, max))
}
